import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import { ContainerQuery } from 'react-container-query';
import { entries, isEmpty, isEqual, map, memoize, noop, uniq } from 'lodash';
import CardsModule from '@wix/wix-vod-cards';
import AnnotoModule from '@wix/wix-vod-annoto/dist/module';
import { SettingsModule } from '@wix/wix-vod-playable-settings';
import HLSAdapter from 'playable/dist/src/adapters/hls';
import { EngineState } from 'playable/dist/src/constants';
import ReactPlayable from 'react-playable';
import { PlayerAPIFacade } from './player-api-facade';
import { encodeHTML } from '../../common/utils/sanitizer';
HLSAdapter.DEFAULT_HLS_CONFIG = {
  ...HLSAdapter.DEFAULT_HLS_CONFIG,
  startLevel: -1 // https://github.com/video-dev/hls.js/blob/master/docs/API.md#testbandwidth
};
const findChangedProps = (currentProps, previousProps) => {
  const uniqKeys = uniq([...Object.keys(currentProps), ...Object.keys(previousProps)]);
  return uniqKeys.filter(key => key !== 'children') // children is handled by React so we can skip it
  .reduce((acc, key) => {
    if (!isEqual(currentProps[key], previousProps[key])) {
      return {
        ...acc,
        [key]: currentProps[key]
      };
    }
    return acc;
  }, {});
};
const makeConfigurationChanger = handlers => changes => {
  entries(changes).forEach(_ref => {
    let [propName, propValue] = _ref;
    if (handlers[propName]) {
      handlers[propName](propValue);
    }
  });
};
const containerQueries = {
  'max-1900px': {
    maxWidth: 1900
  },
  'min-981px': {
    minWidth: 981
  },
  'max-639px': {
    maxWidth: 639
  },
  'min-601px': {
    minWidth: 601
  },
  'min-579px': {
    minWidth: 579
  },
  'max-550px': {
    maxWidth: 550
  },
  'min-460px': {
    minWidth: 460
  },
  'max-400px': {
    maxWidth: 400
  },
  'max-350px': {
    maxWidth: 350
  },
  'min-350px': {
    minWidth: 350
  },
  'max-300px': {
    maxWidth: 300
  },
  'max-280px': {
    maxWidth: 280
  }
};
const getWidthAttribute = (sizes, minOrMax) => Object.keys(sizes).filter(key => key.startsWith(minOrMax) && sizes[key]).map(key => key.replace(`${minOrMax}-`, '')).join(' ');
export class VODPlayer extends React.Component {
  constructor() {
    super();
    /**
     * ReactPlayable will do shallow compare of it's props,
     * so we need to persist this array
     */
    _defineProperty(this, "convertToPlayableSrc", memoize(urls => map(urls, _ref2 => {
      let {
        url,
        type
      } = _ref2;
      return {
        url,
        type
      };
    })));
    _defineProperty(this, "initSettingsModule", playerInstance => {
      const {
        settingsModuleConfig
      } = this.props;
      const onPopoverVisibilityChange = settingsModuleConfig == null ? void 0 : settingsModuleConfig.onPopoverVisibilityChange;
      const config = {
        ...settingsModuleConfig,
        onPopoverVisibilityChange(visible) {
          if (visible) {
            playerInstance.hideCardsContainer();
          } else if (playerInstance.showCardsContainer) {
            playerInstance.showCardsContainer();
          }
          if (onPopoverVisibilityChange) {
            onPopoverVisibilityChange(visible);
          }
        }
      };
      playerInstance.initSettings(config);
    });
    _defineProperty(this, "onInit", playerInstance => {
      const {
        isRTL,
        showSettings
      } = this.props;
      this.player = playerInstance;
      if (showSettings) {
        this.initSettingsModule(playerInstance);
      }
      playerInstance.setRtl(isRTL);
    });
    _defineProperty(this, "handleVideoViewMode", mode => {
      this.player.setVideoViewMode(mode);
    });
    _defineProperty(this, "handleLoop", isLoop => {
      this.player.setLoop(isLoop);
    });
    _defineProperty(this, "handleMuted", muted => {
      if (muted) {
        this.player.mute();
      } else {
        this.player.unmute();
      }
    });
    _defineProperty(this, "handlePlaysInline", isInline => {
      this.player.setPlaysinline(isInline);
    });
    _defineProperty(this, "handlePlayableChanges", targetProps => {
      const changes = findChangedProps(this.props, targetProps);
      if (!isEmpty(changes)) {
        this.changePlayerUI(changes);
      }
      return changes;
    });
    _defineProperty(this, "handlePauseVideoOnFullScreenExit", isMobile => {
      if (isMobile) {
        this.player.enablePauseVideoOnFullScreenExit();
      } else {
        this.player.disablePauseVideoOnFullScreenExit();
      }
    });
    _defineProperty(this, "handlePlaybackStateChanged", playbackState => {
      const {
        onPlay,
        onPause,
        onEnded
      } = this.props;
      const handlers = {
        [EngineState.PLAYING]: onPlay,
        [EngineState.PAUSED]: onPause,
        [EngineState.ENDED]: onEnded
      };
      if (handlers[playbackState]) {
        handlers[playbackState]();
      }
    });
    _defineProperty(this, "handleLogoProperties", logo => {
      if (!logo) {
        this.player.setLogo();
        return;
      }
      this.player.setLogo(logo.src);
      this.player.setAlwaysShowLogo(logo.showAlways);
    });
    _defineProperty(this, "handlePlayAtFullScreen", playAtFullScreen => {
      if (playAtFullScreen) {
        this.player.enableEnterFullScreenOnPlay();
      } else {
        this.player.disableEnterFullScreenOnPlay();
      }
    });
    _defineProperty(this, "handleSetMainUIShouldAlwaysShow", setMainUIShouldAlwaysShow => {
      this.player.setMainUIShouldAlwaysShow(setMainUIShouldAlwaysShow);
    });
    _defineProperty(this, "handleOnTitleClick", onTitleClick => this.player.setTitleClickCallback(onTitleClick));
    _defineProperty(this, "handleCanDownload", canDownload => {
      const {
        onDownloadClick
      } = this.props;
      if (onDownloadClick && canDownload) {
        this.player.setDownloadClickCallback(onDownloadClick);
        this.player.showDownloadButton();
        return;
      }
      this.player.setDownloadClickCallback();
      this.player.hideDownloadButton();
    });
    _defineProperty(this, "handleOnWatchOnSiteClick", onWatchOnSiteClick => {
      this.player.setLogoClickCallback(onWatchOnSiteClick);
    });
    _defineProperty(this, "handlePlaying", _ref3 => {
      let {
        isVideoPlayRequested,
        vidiUrls,
        isVideoPauseRequested
      } = _ref3;
      const shouldTriggerPlay = isEmpty(vidiUrls) ? false : isVideoPlayRequested;
      if (shouldTriggerPlay && isVideoPauseRequested) {
        console.warn('VODPlayer: isVideoPlayRequested and isVideoPauseRequested both true');
        return;
      }
      if (shouldTriggerPlay) {
        this.player.play();
      }
      if (isVideoPauseRequested) {
        this.player.pause();
      }
    });
    _defineProperty(this, "makePlayableConfig", _ref4 => {
      let {
        isAnnotoModuleEnabled,
        disableFullScreen,
        hideOverlay,
        modules,
        hideMainUI
      } = _ref4;
      return {
        modules: {
          cards: CardsModule,
          settings: SettingsModule,
          ...(isAnnotoModuleEnabled && {
            annoto: AnnotoModule
          }),
          ...modules
        },
        disableFullScreen,
        playbackAdapters: [HLSAdapter],
        hideOverlay,
        hideMainUI
      };
    });
    this.player = null;
    this.changePlayerUI = makeConfigurationChanger({
      logo: this.handleLogoProperties,
      playAtFullScreen: this.handlePlayAtFullScreen,
      onTitleClick: this.handleOnTitleClick,
      canDownload: this.handleCanDownload,
      onWatchOnSiteClick: this.handleOnWatchOnSiteClick,
      setMainUIShouldAlwaysShow: this.handleSetMainUIShouldAlwaysShow,
      mobileMode: this.handlePauseVideoOnFullScreenExit,
      muted: this.handleMuted,
      playInline: this.handlePlaysInline,
      loop: this.handleLoop,
      videoViewMode: this.handleVideoViewMode
    });
    this.convertToPlayableSrc.cache = new Map();
  }
  componentDidMount() {
    this.changePlayerUI(this.props);
    this.handlePlaying(this.props);
    this.props.onInit(this.player);
  }
  componentDidUpdate(prevProps) {
    const changes = this.handlePlayableChanges(prevProps);
    if (changes.isVideoPlayRequested || changes.isVideoPauseRequested) {
      this.handlePlaying(this.props);
    }
  }
  render() {
    const {
      children,
      onUserSeek,
      onSeek,
      onMuteChange,
      onVolumeChange,
      vidiUrls,
      autoPlay,
      preload,
      fillAllSpace,
      videoItem,
      canShowTitle,
      texts,
      width,
      height,
      posterUrl,
      onPlayAbort,
      onError,
      onFullScreenChanged
    } = this.props;
    const config = this.makePlayableConfig(this.props);
    const src = this.convertToPlayableSrc(vidiUrls);
    const style = fillAllSpace ? {
      width: '100%',
      height: '100%'
    } : {
      width,
      height
    };
    return /*#__PURE__*/React.createElement(ContainerQuery, {
      query: containerQueries
    }, sizes => /*#__PURE__*/React.createElement("div", {
      style: style,
      "data-vod-player-max-width": getWidthAttribute(sizes, 'max'),
      "data-vod-player-min-width": getWidthAttribute(sizes, 'min')
    }, /*#__PURE__*/React.createElement(ReactPlayable, {
      width: width,
      height: height,
      fillAllSpace: fillAllSpace,
      src: src,
      autoplay: autoPlay,
      preload: preload,
      title: canShowTitle ? encodeHTML(videoItem.title) : null,
      poster: posterUrl,
      texts: texts,
      config: config,
      onInit: this.onInit
    }, /*#__PURE__*/React.createElement(PlayerAPIFacade, {
      onFullScreenChanged: onFullScreenChanged,
      onPlaybackStateChanged: this.handlePlaybackStateChanged,
      onUserSeek: onUserSeek,
      onSeek: onSeek,
      onMute: onMuteChange,
      onVolumeChange: onVolumeChange,
      onPlayAbort: onPlayAbort,
      onError: onError
    }, children))));
  }
}
_defineProperty(VODPlayer, "defaultProps", {
  width: 640,
  height: 360,
  mobileMode: false,
  logo: null,
  preload: 'metadata',
  canShowTitle: true,
  onPause: noop,
  onPlay: noop,
  onEnded: noop,
  onPlayAbort: noop,
  onVideoInfoShow: noop,
  onBuyRentButtonClick: noop,
  onShareVideoShow: noop,
  onFullScreenChanged: noop,
  onWatchOnSiteClick: noop,
  onMuteChange: noop,
  onVolumeChange: noop,
  onSeek: noop,
  onUserSeek: noop,
  onError: noop,
  onInit: noop,
  onAnomalyDetected: noop,
  isAnnotoEnabled: false,
  disableFullScreen: false,
  hideOverlay: true,
  showSettings: false
});
//# sourceMappingURL=vod-player.js.map